/**
 * Costi di acquisto documentali.
 *
 * `unit_price` delle righe PO/DDT è il prezzo unitario prima dello sconto.
 * Questo helper è intenzionalmente separato dai costi manuali, che sono già
 * netti e non devono mai subire una seconda applicazione dello sconto.
 */
import { z } from 'zod';

export const posPurchaseCostSuggestionQuerySchema = z.object({
  product_id: z.string().uuid(),
  supplier_id: z.string().uuid(),
  /** Omission is the exact parent identity, never all variants. */
  variant_id: z.string().uuid().optional(),
}).strict();
export type PosPurchaseCostSuggestionQuery = z.infer<typeof posPurchaseCostSuggestionQuerySchema>;

export const posPurchaseCostSuggestionSchema = z.object({
  product_id: z.string().uuid(),
  supplier_id: z.string().uuid(),
  variant_id: z.string().uuid().nullable(),
  unit_cost: z.number().finite().nonnegative().nullable(),
  status: z.enum(['available', 'no_history', 'incomplete_cost']),
  quantity: z.number().finite().nonnegative(),
  movement_count: z.number().int().nonnegative(),
  source: z.literal('finalized_purchase_loads'),
}).strict();
export type PosPurchaseCostSuggestion = z.infer<typeof posPurchaseCostSuggestionSchema>;

/** Persisted snapshot, not a comparison with today's average. NULL is legacy/manual. */
export const posPurchaseOrderCostSuggestionSchema = z.object({
  state: z.enum(['unconfirmed', 'confirmed', 'manual']),
  suggested_unit_cost: z.number().finite().nonnegative().max(99_999_999.9999).nullable(),
  supplier_id: z.string().uuid(),
  product_id: z.string().uuid(),
  variant_id: z.string().uuid().nullable(),
  source: z.literal('finalized_purchase_loads'),
}).strict().superRefine((value, context) => {
  if (value.state !== 'manual' && value.suggested_unit_cost === null) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['suggested_unit_cost'],
      message: 'Il costo suggerito deve essere disponibile.',
    });
  }
});
export type PosPurchaseOrderCostSuggestion = z.infer<typeof posPurchaseOrderCostSuggestionSchema>;

/** Keep an explicit snapshot; identity changes invalidate it, edits make it manual. */
export function normalizePurchaseOrderCostSuggestion(
  requested: PosPurchaseOrderCostSuggestion | null | undefined,
  persisted: PosPurchaseOrderCostSuggestion | null | undefined,
  identity: { product_id: string; supplier_id: string; variant_id: string | null },
  unitPrice: number | null,
): PosPurchaseOrderCostSuggestion | null {
  const snapshot = requested === undefined ? persisted : requested;
  if (!snapshot || snapshot.product_id !== identity.product_id
    || snapshot.supplier_id !== identity.supplier_id
    || snapshot.variant_id !== identity.variant_id) return null;
  return {
    ...snapshot,
    state: snapshot.state === 'manual' || unitPrice !== snapshot.suggested_unit_cost
      ? 'manual' : snapshot.state,
  };
}

export type PurchaseCostInput = number | string | null | undefined;

function finiteNumber(value: PurchaseCostInput): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Arrotondamento compatibile con numeric(12,4). */
export function roundPurchaseCost4(value: number): number {
  return Math.round((value + Number.EPSILON) * 10_000) / 10_000;
}

/**
 * Converte il prezzo documentale pre-sconto nel costo netto immutabile.
 *
 * L'assenza del prezzo resta `null`, mentre 0 è un costo valido. Gli sconti
 * mancanti sono 0; valori esplicitamente fuori intervallo vengono limitati
 * allo stesso dominio commerciale [0,100] usato dalle righe documento.
 */
export function netUnitCostFromPurchaseLine(
  unitPrice: PurchaseCostInput,
  discountPercent: PurchaseCostInput = 0,
): number | null {
  const gross = finiteNumber(unitPrice);
  if (gross === null) return null;
  const discountInput = finiteNumber(discountPercent);
  const discount = Math.min(100, Math.max(0, discountInput ?? 0));
  return roundPurchaseCost4(gross * (1 - discount / 100));
}